import {ReactNode, useContext, useEffect, useRef, useState} from "react";
import {makeStyles} from "tss-react/mui";
import {Box, LinearProgress, TextField, Typography, CircularProgress} from "@mui/material";
import {APIState, INotificationSeverity, NotificationContext, useApi} from "@buildwitt/shapeshift-identity";
import {IEntity, IEntityFilter, IPaginatedResponse} from "@buildwitt/shapeshift-types";
import {EnhancedTableHeadOrder} from "./EnhancedTableHead";
import {EntityIdentifier} from "@buildwitt/shapeshift-types/entityService";
import {useDebounce} from "@buildwitt/shapeshift-common/v1/hooks";
import {DataGridPro, DataGridProProps, GridColDef, GridFilterItem, GridFilterModel, GridLinkOperator, GridRowId, GridRowModel, GridRowParams, GridSelectionModel, GridSortModel, GridValidRowModel, MuiEvent, useGridApiRef} from "@mui/x-data-grid-pro";
import {Delete} from "@mui/icons-material";
import {AdminTableCustomToolbar} from "./AdminTableCustomToolbar";
import {AdminTableColumnsPanel} from "./AdminTableColumnsPanel";
import {DatePicker, LocalizationProvider} from "@mui/x-date-pickers";
import * as pkg from "@mui/x-date-pickers/AdapterDayjs/index.js";
const {AdapterDayjs} = pkg;
import {IOperator} from "@buildwitt/shapeshift-types/IOperator";
import {AdminTableStringFilterInput} from "./AdminTableStringFilterInput";
import {AdminTableNumericFilterInput} from "./AdminTableNumericFilterInput";
import usePersistedFilterModel from "../../../hooks/usePersistedFilterModel";
import {ConfirmDeleteEntityDialog} from "@buildwitt/shapeshift-common/v2/admin";
import {IProps as DeleteDialogProps} from "@buildwitt/shapeshift-common/v2/admin/containers/confirmDeleteEntityDialog";
import {getFromLocalStorage} from "@/utils/common-utilities";
import useDevCycleSdk from "../../../hooks/useDevCycleSdk";

export interface IBaseSearchDTO<T extends IEntity = IEntity> {
	page: number,
	pageSize: number,
	searchTerm: string,
	dateFrom?: string;
	dateTo?: string;
	filters?: {
		operator: GridLinkOperator | undefined;
		conditions: IEntityFilter<T>[];
	}
}

interface IPropsComponentOverrides<T extends IEntity> {
	deleteDialog?: (e: DeleteDialogProps<T>) => JSX.Element,
}

export interface IProps<K extends EntityIdentifier, T extends GridValidRowModel & IEntity = (IEntity & GridValidRowModel), S = IBaseSearchDTO> extends Omit<DataGridProProps, "rows" | "columns" | "data"> {
	data?: IPaginatedResponse<T[]>,
	url: string,
	onEditAction?: (e: T) => void,
	children?: (data: T[], reload: () => void, searchDTO: S, apiResult: APIState<IPaginatedResponse<T[]>>, clearSelection: () => void) => JSX.Element | JSX.Element[],
	columns: (GridColDef<T> & {type: "date" | "string" | "number" | "boolean"; columnPanelGroup?: string})[],
	enableQuickFilter?: boolean,
	enableDelete?: boolean | ((row: T) => boolean),
	disableDeleteHide?: boolean,
	deleteHeaderName?:string,
	type: K,
	bulkActionOptions?: {label: string, onSelect: (label: string, selected: number[]) => void}[],
	searchDTOverride?: (e: S) => Partial<S>,
	componentOverrides?: IPropsComponentOverrides<T>,
	filterSettingsPersistenceStorageKey?: string,
	disableExport?: boolean,
	hideExport?: boolean,
	checkboxSelection?: boolean,
	toolBarText?: string,
	exportToolTipText?: string,
	showAlternativeExportoption?: boolean;
	isExporting?: boolean;
	onClickAlternativeExportOption?: () => void;
	updatedFilterCallback?: (operator: GridLinkOperator, conditions: IEntityFilter<T>[]) => void;
	updatedSortCallback?: (order: string, orderBy: string) => void;
	enableAdvancedStringFilters?: boolean;
	isLoadingCallback?: (isLoading: boolean) => void;
	customToolbarOptions?: ReactNode;
	rowSelectionCallback?: (selectedRows: Map<GridRowId, GridRowModel>) => void;
}

const cohortBaseUrl = "/api/strapi-plugin-shapeshift-lms/cohort";

const useStyles = makeStyles()((theme) => ({
	adminTable: {
		"& svg path": {
			fill: theme.palette.common.white
		},
		"& th": {
			fontSize: "11px",
			fontWeight: 700,
			textTransform: "uppercase"
		}
	},
	adminTableContainer: {
		display: "flex",
		flexDirection: "column"
	},
	filtersContainer: {
		padding: "50px 0 20px 0",
		display: "flex",
		gap: "10px",
		"& svg": {
			width: "16px"
		},
		"& svg path": {
			fill: theme.palette.common.white
		},
		"& > *": {
			textAlign: "center",
			minWidth: "26px",
			borderRadius: "5px",
			border: "1px solid " + theme.palette.grey[900],
			padding: "5px"
		},
		"& input": {
			textAlign: "left",
			width: "200px",
			paddingLeft: "10px",
			background: theme.palette.background.paper,
			color: theme.palette.common.white
		}
	},
	dateInput: {
		width: "180px",
		"& .MuiInputAdornment-root": {
			"& svg path": {
				fill: theme.palette.common.white
			}
		}
	},
	filtersContainerSpacer: {
		flexGrow: 10,
		border: "none"
	},
	iconCell: {
		zIndex: 10,
		display: "flex",
		gap: "15px",
		justifyContent: "right",
		"& > *": {
			zIndex: 10,

			cursor: "pointer"
		},
		"& svg": {
			height: "0.99em"
		}
	},
	checkBoxCell: {
		"& > *": {
			zIndex: 10,
			cursor: "pointer"
		}
	},
	editActionCover: {
		position: "absolute",
		zIndex: 0, left: 0,
		right: 0,
		top: 0,
		bottom: 0,
		cursor: "pointer"
	},
	noRowsOverlay: {
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		height: "100%",
		padding: 2
	},
	noRowsContainer: {
		position: "absolute",
		display: "flex",
		flexDirection: "column",
		width: "auto",
		height: "104px",
		minHeight: "calc(100% + 0px)",
		alignItems: "center",
		justifyContent: "center",
		backgroundColor: "#191919",
		borderRadius: "2px",
		top: 0,
		left: 0,
		right: 0,
		bottom: 0
	},
	noRowsText: {
		color: "#fff",
		fontWeight: 400,
		fontSize: "15px",
		lineHeight: 1.43,
		fontFamily: "Titillium Web, sans-serif"
	}
}));

const NoRowsOverlay = ({isLoading, hasData, statusCode, showNoRows, classes}: {isLoading: boolean, hasData: boolean, statusCode?: number, showNoRows: boolean, classes: any}): JSX.Element => {
	const shouldShowSpinner = isLoading || (!hasData && !statusCode);
	return (
		<Box className={classes.noRowsOverlay}>
			{shouldShowSpinner ? (
				<CircularProgress />
			) : showNoRows ? (
				<Box className={classes.noRowsContainer}>
					<Typography variant="body2" className={classes.noRowsText}>
						No rows
					</Typography>
				</Box>
			) : (
				<CircularProgress />
			)}
		</Box>
	);
};

export const AdminTable = <K extends EntityIdentifier, T extends IEntity, S extends IBaseSearchDTO = IBaseSearchDTO>({
	children,
	data,
	url,
	type,
	enableQuickFilter,
	enableDelete,
	disableDeleteHide,
	deleteHeaderName = "",
	columns,
	searchDTOverride,
	bulkActionOptions,
	onEditAction,
	componentOverrides,
	filterSettingsPersistenceStorageKey = "",
	disableExport,
	hideExport,
	toolBarText,
	exportToolTipText,
	checkboxSelection = true,
	showAlternativeExportoption = false,
	isExporting = false,
	onClickAlternativeExportOption,
	updatedFilterCallback,
	updatedSortCallback,
	isLoadingCallback,
	enableAdvancedStringFilters = false,
	customToolbarOptions,
	rowSelectionCallback,
	components: userDataGridComponents,
	...baseProps}: IProps<K, T, S>): JSX.Element => {
	const mapOp = (e: GridFilterItem): IEntityFilter<T> => {
		return {operator: e.operatorValue as IOperator, attribute: e.columnField as any, value: encodeURIComponent(e.value)};
	}
	const {classes} = useStyles();
	const {filterSettingsPersistence} = useDevCycleSdk();
	const [apiResult, getRows] = useApi<IPaginatedResponse<T[]>>(null, data || {data: [], totalCount: 0, page: 1, pageSize: 10})
	const {data: rows, isLoading, statusCode} = apiResult
	const [showNoRows, setShowNoRows] = useState(false)
	const [order, setOrder] = useState<EnhancedTableHeadOrder>("asc");
	const [orderBy, setOrderBy] = useState<keyof T>();
	const [searchDTO, setSearchDTO] = useState<S>(() => {
		const paginationSettings = {page: 1, pageSize: 25};

		if (filterSettingsPersistence){
			const filterModel = getFromLocalStorage<GridFilterModel>(filterSettingsPersistenceStorageKey);
			if (filterModel){
				return {
					...paginationSettings,
					searchTerm: filterModel.quickFilterValues?.[0],
					filters: {
						operator: filterModel.linkOperator,
						conditions: filterModel.items.map(e => mapOp(e))
					}
				} as S
			}
		}

		return {
			...paginationSettings,
			searchTerm: ""
		} as S
	});
	const searchDTODebounced = useDebounce(searchDTO, 100);
	const {notify} = useContext(NotificationContext);
	const buttonContainerRef = useRef<HTMLDivElement>(null);
	const [selected, setSelected] = useState<number[]>([]);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState<{open: boolean, row: T}>();
	const [deleteApiResponse, deleteApi] = useApi<T, FormData>();
	const apiRef = useGridApiRef();
	const [spacingModified, setSpacingModified] = useState(false);
	const tableKey = url.replace(/\//g, "-");
	const [persistedFilterModel, handleFilterModelChange] = usePersistedFilterModel(filterSettingsPersistenceStorageKey);
	const gridFilterModel = filterSettingsPersistenceStorageKey !== "" && filterSettingsPersistence ? persistedFilterModel : undefined;

	const [columnVisibilityModel, setColumnVisibilityModel] = useState<{ [key: string]: boolean }>(() => {
		const saved = localStorage.getItem(tableKey + "ColumnVisability");
		return saved ? JSON.parse(saved) : (baseProps?.initialState?.columns?.columnVisibilityModel || {});
	});

	const assignFilters = (_column: typeof columns[0]): GridColDef<T> => {
		const column = {..._column}

		if(column.filterOperators)
			return column;

		const dateField = (props: any): JSX.Element => {
			return (
				<DatePicker
					label="Value"
					renderInput={(params) => <TextField {...params} className={classes.dateInput} variant="standard" />}
					value={props.item.value ? new Date(props.item.value) : null} // Ensure it's a Date object
					onChange={(newValue) => {
						props.applyValue({
							...props.item,
							value: newValue ? newValue.toISOString() : null // Store as ISO string or other format needed
						});
					}}
				/>
			);
		};

		// Define filter operators for specific types
		const stringFilterOperators = ([
			{label: "contains", value: "co", InputComponent: AdminTableStringFilterInput},
			{label: "equals", value: "eq", InputComponent: AdminTableStringFilterInput},
			{label: "not equal to", value: "ne", InputComponent: AdminTableStringFilterInput},
			{label: "starts with", value: "sw", InputComponent: AdminTableStringFilterInput},
			{label: "ends with", value: "ew", InputComponent: AdminTableStringFilterInput},
			...(!enableAdvancedStringFilters ? [
				{label: "exists", value: "pr"}
			] : []),
			...(enableAdvancedStringFilters ? [
				{label: "is empty", value: "ie"},
				{label: "is not empty", value: "ine"}
			] : [])
		])

		const numberFilterOperators = ([
			{label: "greater than", value: "gt", InputComponent: AdminTableNumericFilterInput},
			{label: "less than", value: "lt", InputComponent: AdminTableNumericFilterInput},
			{label: "greater than or equal to", value: "ge", InputComponent: AdminTableNumericFilterInput},
			{label: "less than or equal to", value: "le", InputComponent: AdminTableNumericFilterInput},
			{label: "equals", value: "eq", InputComponent: AdminTableNumericFilterInput},
			{label: "not equal to", value: "ne", InputComponent: AdminTableNumericFilterInput}
		])

		const dateFilterOperators = ([
			{label: "greater than", value: "gt", InputComponent: dateField},
			{label: "less than", value: "lt", InputComponent: dateField},
			{label: "greater than or equal to", value: "ge", InputComponent: dateField},
			{label: "less than or equal to", value: "le", InputComponent: dateField},
			{label: "equals", value: "eq", InputComponent: dateField},
			{label: "not equal to", value: "ne", InputComponent: dateField},
			...(enableAdvancedStringFilters ? [
				{label: "is empty", value: "ie"},
				{label: "is not empty", value: "ine"}
			] : [])
		])

		// Add filter operators based on inferred field type
		if (column.type === "string") {
			column.filterOperators = stringFilterOperators.map(e => ({...e, getApplyFilterFn: () => null}));
		} else if (column.type === "number") {
			column.filterOperators = numberFilterOperators.map(e => ({...e, getApplyFilterFn: ()=> null}));
		} else if (column.type === "date") {
			column.filterOperators = dateFilterOperators.map(e => ({...e, getApplyFilterFn: ()=> null}));
		}

		return column;
	}

	const handleRequestSort = (e: GridSortModel): void => {
		const sort = e?.[0]?.sort ?? "asc";
		const field = e?.[0]?.field;

		setOrder(sort);
		setOrderBy(field as keyof T);
		!!updatedSortCallback && updatedSortCallback(sort, field);
	};

	useEffect(() => {
		isLoadingCallback && isLoadingCallback(isLoading || !rows || !statusCode);
	}, [isLoading, rows, statusCode])

	useEffect(() => {
		if (!isLoading && statusCode && (!rows?.data || rows.data.length === 0)) {
			// Add a small delay to prevent flashing "No rows" during data loading
			const timer = setTimeout(() => {
				setShowNoRows(true);
			}, 100);
			return () => clearTimeout(timer);
		} else {
			setShowNoRows(false);
		}
	}, [isLoading, statusCode, rows])


	useEffect(() => {
		const payload: {[key in keyof S]?: string | Date | number | boolean} = {};

		const dto = {...searchDTODebounced, ...(searchDTOverride?.(searchDTODebounced) || {})};

		for(const e of Object.keys(dto)){
			const key = e as keyof S;
			if(typeof dto[key] === "object"){
				payload[key] = JSON.stringify(dto[key])
			}
			else payload[key] = dto[key] as any;
		}
		getRows({
			url,
			data: {...payload, order, orderBy}
		})
	}, [order, orderBy, searchDTODebounced, searchDTOverride])

	const handleRowClick = (params: GridRowParams<T>, event: MuiEvent<React.MouseEvent<HTMLElement, MouseEvent>>): void => {
		if(buttonContainerRef.current?.contains(event.target as Node)){
			return;
		}
		onEditAction?.(params.row)
	}

	useEffect(() => {
		if(!deleteApiResponse.statusCode)
			return;

		if(deleteApiResponse.statusCode === 200){
			notify("Deleted " + type, "Success", INotificationSeverity.success, 5000);
			getRows();
		}
		else {
			notify("Please try again ", "Failed to delete " + type, INotificationSeverity.error, 5000)
		}
	}, [deleteApiResponse])

	const handleDelete = (): void=> {
		if(!deleteDialogOpen?.row)
			return;

		deleteApi({
			url: cohortBaseUrl + "/" + type + `/${deleteDialogOpen?.row.id}`,
			method: "DELETE"
		});
		setDeleteDialogOpen(undefined)
	}

	const deleteProps: DeleteDialogProps<T> = {
		onConfirm: handleDelete,
		open: !!deleteDialogOpen?.open,
		entity: deleteDialogOpen?.row,
		onClose: () => setDeleteDialogOpen(undefined)
	};

	useEffect(() => {
		apiRef.current.subscribeEvent("columnOrderChange", () => {
			const orderedFields = apiRef.current.getAllColumns().map(col => col.field);
			localStorage.setItem(tableKey + "ColumnOrder", JSON.stringify(orderedFields));
			setSpacingModified(true)
		});

		apiRef.current.subscribeEvent("columnWidthChange", () => {
			const columnWidths: Record<string, number> = {};
			apiRef.current.getAllColumns().forEach(col => {
				if (col.field) {
					columnWidths[col.field] = col.computedWidth || col.width || 100;
				}
			});
			setSpacingModified(true)
			localStorage.setItem(tableKey + "ColumnWidths", JSON.stringify(columnWidths));
		});

		if(localStorage.getItem(tableKey + "ColumnOrder") || localStorage.getItem(tableKey + "ColumnWidths") || localStorage.getItem(tableKey + "ColumnVisability")){
			setSpacingModified(true);
		}
	}, [apiRef]);

	useEffect(() => {
		const selectedRows = apiRef.current.getSelectedRows();
		rowSelectionCallback && rowSelectionCallback(selectedRows);
	}, [selected])

	const handleColumnVisibilityChange = (newModel: { [key: string]: boolean }): void => {
		setColumnVisibilityModel(newModel);
		setSpacingModified(true)
		localStorage.setItem(tableKey + "ColumnVisability", JSON.stringify(newModel));
	};

	const storedColumnOrder: any = JSON.parse(localStorage.getItem(tableKey + "ColumnOrder") || "[]");
	const storedColumnWidths: Record<string, number> = JSON.parse(localStorage.getItem(tableKey + "ColumnWidths") || "{}");

	const resetSpacingAndOrder = (): void => {
		localStorage.removeItem(tableKey + "ColumnOrder");
		localStorage.removeItem(tableKey + "ColumnWidths");
		localStorage.removeItem(tableKey + "ColumnVisability");
		setSpacingModified(false);
		setColumnVisibilityModel(baseProps?.initialState?.columns?.columnVisibilityModel || {});
		getRows();
	};

	type TableColumn = (typeof columns)[number];
	const orderedColumns: typeof columns = ((): TableColumn[] => {
		if (!storedColumnOrder.length) {
			return columns;
		}
		const fromStorage = storedColumnOrder
			.map((field: string) => columns.find((col) => col.field === field))
			.filter((col): col is TableColumn => Boolean(col));
		const seen = new Set(fromStorage.map((col) => col.field));
		const appended = columns.filter((col) => !seen.has(col.field));
		// Saved order from localStorage omits columns added after the user reordered; append them so new fields stay visible and appear in the column panel.
		return appended.length ? [...fromStorage, ...appended] : fromStorage;
	})();

	const finalColumns = orderedColumns.map((col: TableColumn) => ({
		...col,
		width: storedColumnWidths[col.field] || col.width,
		// Preserve flex only if width is not explicitly set
		flex: storedColumnWidths[col.field] ? undefined : col.flex
	}));

	const handleSelectRow = (selectionModel: GridSelectionModel): void => {
		setSelected(selectionModel as number[]);
	}

	const Toolbar = (): JSX.Element => <AdminTableCustomToolbar
		enableQuickFilter={enableQuickFilter}
		canReset={spacingModified}
		onReset={resetSpacingAndOrder}
		type={type}
		selected={selected as number[]}
		bulkActionOptions={bulkActionOptions}
		disableExport={disableExport}
		hideExport={hideExport}
		toolBarText={toolBarText}
		exportToolTipText={exportToolTipText}
		showAlternativeExportoption={showAlternativeExportoption}
		isExporting={isExporting}
		onClickAlternativeExportOption={onClickAlternativeExportOption}
		customToolbarOptions={customToolbarOptions}
	/>

	return (
		<Box className={classes.adminTable}>
			<LocalizationProvider dateAdapter={AdapterDayjs}>
				<Box className={classes.adminTableContainer}>
					<DataGridPro<T>
						apiRef={apiRef}
						aria-label="admin table"
						autoHeight
						loading={isLoading}
						sortingMode="server"
						page={(searchDTO.page - 1)}
						keepNonExistentRowsSelected
						columnVisibilityModel={columnVisibilityModel}
						onColumnVisibilityModelChange={handleColumnVisibilityChange}
						pageSize={searchDTO.pageSize}
						onPageSizeChange={e => setSearchDTO(prev => ({...prev, pageSize: e}))}
						onPageChange={e => setSearchDTO(prev => ({...prev, page: (e + 1)}))}
						checkboxSelection={checkboxSelection}
						onSelectionModelChange={e => handleSelectRow(e)}
						selectionModel={selected}
						paginationMode="server"
						onRowClick={(params, event) => handleRowClick(params, event)}
						pagination
						rowCount={rows?.totalCount ?? 0}
						onSortModelChange={e => handleRequestSort(e)}
						rows={rows?.data ?? []}
						filterModel={gridFilterModel}
						columns={[...finalColumns.map(e => assignFilters(e)), ...(enableDelete ? [{
							field: "delete",
							headerName: deleteHeaderName,
							filterable: false,
							...(disableDeleteHide && {hideable: false}),
							width: 50,
							renderCell: (params) => {
								if(typeof enableDelete === "function" && !enableDelete(params.row))
									return <></>;
								return <Box ref={buttonContainerRef} onClick={(e) => {e.stopPropagation(); setDeleteDialogOpen({open: true, row: params.row})}}><Delete sx={theme => ({"path": {fill: theme.palette.common.white}})}/></Box>
							}
						}] : [])]}
						filterMode="server"
						onFilterModelChange={(filterModel, details) => {
							const filters = {
								operator: filterModel.linkOperator,
								conditions: filterModel.items.map(e => mapOp(e))
							};

							setSearchDTO(prev => ({
								...prev,
								searchTerm: filterModel.quickFilterValues?.[0],
								filters
							}));
							!!filterSettingsPersistenceStorageKey && filterSettingsPersistence && handleFilterModelChange(filterModel, details);
							!!updatedFilterCallback && updatedFilterCallback(filters.operator, filters.conditions);
						}}
						components={{
							Toolbar,
							ColumnsPanel: AdminTableColumnsPanel,
							NoRowsOverlay: () => <NoRowsOverlay isLoading={isLoading} hasData={!!(rows?.data && rows.data.length > 0)} statusCode={statusCode} showNoRows={showNoRows} classes={classes} />,
							...userDataGridComponents
						}}
						rowsPerPageOptions={[25, 50, 100, 200]}
						{...baseProps}
					/>
				</Box>
			</LocalizationProvider>
			{(isLoading || (!rows?.data || rows.data.length === 0) && !statusCode) && <LinearProgress />}
			{children?.(rows?.data ?? [], () => getRows(), searchDTO, apiResult, () => setSelected([]))}
			{componentOverrides?.deleteDialog ? componentOverrides?.deleteDialog(deleteProps) : <ConfirmDeleteEntityDialog {...deleteProps}/>}
		</Box>
	);
}